import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { SECTORS, WORK_PROJECTS } from "../data/work";
import { FadeUp } from "../components/Motion";
import { LedgerMarquee } from "../components/Marquee";

const FILTERS = SECTORS;

const Work = () => {
  const [filter, setFilter] = useState("All");
  const list = filter === "All" ? WORK_PROJECTS : WORK_PROJECTS.filter((c) => c.sector === filter);

  return (
    <>
      <PageHeader
        tag="Work — The 12 year ledger"
        title={
          <>
            86 fights.
            <br />
            <span className="text-nexa-red">Zero fiction.</span>
          </>
        }
        sub="Every project below was executed at Myndroot — pulled straight from the agency's own database. Covers, sectors and years are the real record Nexa Studio walks in with."
      />

      <div className="mx-auto max-w-[1600px] px-5 py-10 md:px-10">
        <div className="flex flex-wrap gap-3" role="tablist" aria-label="Filter work by sector">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
              className={`border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-200 ${
                filter === f
                  ? "border-nexa-red bg-nexa-red text-white"
                  : "border-white/20 text-nexa-bone/60 hover:border-nexa-red hover:text-nexa-red"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <section className="mx-auto max-w-[1600px] px-5 pb-28 md:px-10" data-testid="work-grid">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((c, i) => (
            <FadeUp key={c.name} delay={(i % 3) * 0.08}>
              <Link
                to={`/work/${c.id}`}
                className="group relative flex h-64 flex-col justify-between overflow-hidden border border-white/10 bg-nexa-surface p-6 transition-colors duration-300 hover:border-nexa-red"
                data-testid={`work-item-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {c.img && (
                  <div className="absolute inset-0">
                    <img src={c.img} alt={c.name} loading="lazy" className="h-full w-full object-cover opacity-45 grayscale transition-all duration-500 group-hover:opacity-70 group-hover:grayscale-0" />
                    <div className="absolute inset-0 bg-gradient-to-t from-nexa-black via-nexa-black/40 to-transparent" />
                  </div>
                )}
                <div className="relative flex items-start justify-between">
                  <span className="font-mono text-xs tracking-[0.25em] text-nexa-red">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-nexa-bone/60">{c.sector}{c.year ? ` · ${c.year}` : ""}</span>
                </div>
                <div className="relative">
                  <h2 className="font-display text-2xl uppercase leading-tight text-nexa-bone transition-colors duration-300 group-hover:text-nexa-red md:text-3xl">
                    {c.name}
                  </h2>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-nexa-bone/50">{c.terms.join(" · ")}</p>
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
        <p className="mt-10 font-mono text-xs uppercase tracking-[0.2em] text-nexa-bone/40" data-testid="work-count">
          {list.length} projects shown — real records from the Myndroot database, carried forward into Nexa Studio.
        </p>
        <FadeUp className="mt-8">
          <Link
            to="/case-studies"
            data-testid="work-cta-case-studies"
            className="group inline-flex items-center gap-3 border border-white/25 px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-nexa-bone transition-colors duration-200 hover:border-nexa-red hover:text-nexa-red"
          >
            Go deeper — three fights in full
            <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </FadeUp>
      </section>

      <LedgerMarquee names={WORK_PROJECTS.map((c) => c.name)} />
    </>
  );
};

export default Work;
